/**
 * Workspace Metrics Service
 *
 * Service layer for workspace analytics and metrics API calls.
 * Provides functions to fetch workspace analytics data and token usage history.
 */

import apiClient from './axiosConfig';
import { WorkspaceAnalytics, TokenHistoryPoint } from '../types/metrics';

/**
 * Get comprehensive analytics for a workspace
 *
 * @param workspaceId - ID of the workspace
 * @param periodDays - Analysis period in days (default: 30)
 * @returns Promise resolving to workspace analytics data
 * @throws Error if user doesn't have can_view_analytics permission
 */
export const getWorkspaceAnalytics = async (
  workspaceId: number,
  periodDays: number = 30
): Promise<WorkspaceAnalytics> => {
  const response = await apiClient.get<WorkspaceAnalytics>(
    `/workspaces/${workspaceId}/analytics`,
    {
      params: { period_days: periodDays },
    }
  );
  return response.data;
};

/**
 * Get daily token usage history for a workspace
 *
 * @param workspaceId - ID of the workspace
 * @param days - Number of days of history to fetch (default: 30)
 * @returns Promise resolving to array of daily token usage points
 * @throws Error if user doesn't have can_view_analytics permission
 */
export const getTokenHistory = async (
  workspaceId: number,
  days: number = 30
): Promise<TokenHistoryPoint[]> => {
  const response = await apiClient.get<TokenHistoryPoint[]>(
    `/workspaces/${workspaceId}/analytics/token-history`,
    {
      params: { days },
    }
  );
  return response.data;
};
